import type { StoryDebugData } from '../context/ChatContext'

import { apiFetch, apiFetchRaw, type HttpMethod } from './apiClient'

export interface UserSessionBrief {
  id: string
  character: string
  storyPath: string
  lastActive: string
}

export interface AuditMsg {
  role: string
  content: string
  type?: string
  position: number
}

export interface AuditRequest {
  id: string
  llmType: string
  model: string
  response: string
  metadata?: Record<string, unknown>
  createdAt: string
  messages: AuditMsg[]
}

export interface AuditSessionSummary {
  id: string
  createdAt: string
  requestCount: number
  messageCount: number
}

export interface AuditSessionDetail {
  id: string
  createdAt: string
  requests: AuditRequest[]
}

export interface MessageFrequency {
  content: string
  role: string
  count: number
}

export interface AuditStats {
  sessionCount: number
  requestCount: number
  messageCount: number
  topUserMessages: MessageFrequency[]
  topAssistantMessages: MessageFrequency[]
}

export interface LLMMessage {
  role: string
  content: string
  type?: string
  /** Joins an assistant message to the MotionDiag records carrying the same
   *  number. Absent on user messages and on anything not produced by a turn. */
  turn?: number
}

export interface SessionSummary {
  id: string
  character: string
  storyPath: string
  connections: number
  messageCount: number
  lastActive: string
  features: string[]
  storyPhase?: string
  turnCount?: number
}

export type { StoryDebugData } from '../context/ChatContext'

/** One gesture tag's journey through the motion pipeline. Mirrors the Go side's
 *  session.MotionDiag. Carries pool entry IDs only — never track payloads.
 *
 *  Timings are per STAGE, with no total: rewrites run concurrently, so a tag's
 *  wall-clock would include waiting on its siblings. Add the two for a headline
 *  number. */
export interface MotionDiag {
  turn: number
  /** Rewriter round trip alone. Absent for a vocabulary match or a cache hit. */
  rewriteMs?: number
  /** Inside gesturegen: the pool read, plus the kimodo /generate round trip when
   *  it generated. With `generated` true this is effectively the kimodo time. */
  resolveMs?: number
  /** A FRESH kimodo clip rather than one the pool already had. */
  generated?: boolean
  rawTag: string
  tagSource: string
  matched: boolean
  rewritten?: string
  preset?: string
  durationSec?: number
  /** Where the clip length came from: the character's own tag, the rewriter's
   *  inference, or gesturegen's flat default. */
  durationSource?: 'tag' | 'rewriter' | 'default'
  outcome: MotionOutcome
  poolEntryId?: string
  motionClass?: string
  error?: string
  /** This record belongs to an LLM attempt that was retried and thrown away.
   *  The motion was processed — generation included — but never performed. */
  discarded?: boolean
}

/** Terminal states, matching the Go constants so a UI badge and a grepped log
 *  line say the same word. */
export type MotionOutcome =
  | 'matched'
  | 'served'
  | 'preset'
  | 'declined'
  | 'not_served'
  | 'corrupt_tracks'
  | 'error'
  | 'rewrite_failed'

export interface SessionMetrics {
  llm_first_sentence_ms?: number
  tts_fast_ms?: number
  tts_quality_ms?: number
  a2f_processing_ms?: number
  stream_total_ms?: number
  first_frame_ms?: number
  total_ms?: number
  storyteller_ms?: number
}

export interface SessionDetail extends SessionSummary {
  history: LLMMessage[]
  conversationSummary?: string
  completedObjectives?: Record<string, boolean>
  playerState?: Record<string, string>
  lastStorytellerReasoning?: string
  motionDiagnostics?: MotionDiag[]
  storyDebug?: StoryDebugData
  lastMetrics?: SessionMetrics
}

// Matches the Go side's domain.AdminConfig — the shape GET /admin/config returns.
export interface AdminConfig {
  feature_overrides?: Record<string, boolean>
  catchphrase_similarity_threshold?: number
}

export interface StoryFacts {
  public: string[]
  private: string[]
}

export interface StoryObjective {
  id: string
  description: string
  completionType: string
  condition?: string
  choice?: string
  urgency: string
  tactics: string[]
}

export interface StoryTrigger {
  id: string
  condition: string
  goto: string
  priority: number
}

export interface StoryPhase {
  description: string
  tone: string
  maxTurns: number
  objectives: StoryObjective[]
  facts: StoryFacts
  triggers: StoryTrigger[]
  onComplete: string
  onFailure: string
}

export interface StoryDetail {
  id: string
  title: string
  version: string
  description: string
  startPhase: string
  character: { name: string; personality: string }
  globalFacts: StoryFacts
  phases: Record<string, StoryPhase>
}

export interface StoryListItem {
  id: string
  title: string
  version: string
  description: string
  characterName: string
  characterVersion: number
  sourceFilename: string
  inDatabase: boolean
  onDisk: boolean
}

export interface CharacterListItem {
  id: string
  name: string
  version: number
  storyCount: number
  createdAt: string
  updatedAt: string
}

export interface CharacterDetail {
  id: string
  name: string
  version: number
  personality: string
  dynamicMotion: boolean
  gesturePoolEnabled: boolean
  stories: { id: string; title: string; sourceFilename: string }[]
  createdAt: string
  updatedAt: string
}

export interface DbPrefabDetail {
  id: string
  characterName: string
  name: string
  phrases: string[]
  alternate: string
  notFor: string[]
  stories: { id: string; title: string; sourceFilename: string }[]
  createdAt: string
  updatedAt: string
}

export interface GestureSynonym {
  stemWords: string
  gestureName: string
}

export interface GestureAnimation {
  id: string
  durationMs: number
  bodyWeight: number
  isAdditive: boolean
  bodyGlbUrl: string
  faceJsonUrl: string
  createdAt: string
}

export interface GestureDTO {
  id: string
  name: string
  position: number
  animation: GestureAnimation | null
}

export interface CharacterAnimConfig {
  gestures: GestureDTO[]
  vocalCues: string[]
  gestureSynonyms: GestureSynonym[]
  a2fConfig: Record<string, unknown>
}

// GesturePoolStats mirrors the Go gesturePoolStatsDTO
// (GET /admin/characters/{id}/gesture-pools/stats). targetPoolSize is served
// so the UI never hardcodes it; an empty pool is all zeros with topKeys: [].
export interface GesturePoolTopKey {
  gestureKey: string
  keyKind: string
  variants: number
  plays: number
}

export interface GesturePoolStats {
  characterId: string
  targetPoolSize: number
  totalEntries: number
  distinctKeys: number
  keysAtTarget: number
  meanEntriesPerKey: number
  medianEntriesPerKey: number
  source: { live: number; topup: number; prewarm: number; import: number }
  motionClass: { gesture: number; performance: number }
  totalPlays: number
  lastPlayedAt: string | null
  oldestCreatedAt: string | null
  newestCreatedAt: string | null
  topKeys: GesturePoolTopKey[]
}

// GesturePoolEntrySummary mirrors the Go gesturePoolEntrySummaryDTO
// (GET /admin/characters/{id}/gesture-pools) — one row per pool variant, no raw
// tracks payload.
export interface GesturePoolEntrySummary {
  id: string
  characterId: string
  gestureKey: string
  keyKind: string
  prompt: string
  seed?: number
  motionClass: string
  durationSec?: number
  source: string
  playCount: number
  createdAt: string
  lastPlayedAt?: string
}

export interface DbCachedResponseDetail {
  id: string
  name: string
  phrases: string[]
  notFor: string[]
  response: string
  animations: string
  stories: { id: string; title: string; sourceFilename: string; characterName: string; characterVersion: number; hasAudio: boolean }[]
  createdAt: string
  updatedAt: string
}

// `createAdminApi` is kept as a zero-arg factory so existing page call sites
// only need to drop the apiKey argument. All requests now ride on the
// `avataros_session` cookie (set by portal SSO); X-API-Key is gone.
export function createAdminApi() {
  function request<T>(url: string, method: string, body?: unknown): Promise<T> {
    return apiFetch<T>(url, { method: method as HttpMethod, ...(body !== undefined ? { body } : {}) })
  }

  return {
    getConfig: () => request<AdminConfig>('/admin/config', 'GET'),
    regenCachedAudio: () => request('/admin/cached-responses/regenerate', 'POST'),
    getFeatures: () => request<Record<string, boolean>>('/admin/features', 'GET'),
    setFeatures: (data: Record<string, boolean>) => request('/admin/features', 'PUT', data),
    getSessions: () => request<SessionSummary[]>('/admin/sessions', 'GET'),
    getSession: (id: string) => request<SessionDetail>(`/admin/sessions/${id}`, 'GET'),
    getAuditSessions: () => request<AuditSessionSummary[]>('/admin/audit/sessions', 'GET'),
    getAuditSession: (id: string) => request<AuditSessionDetail>(`/admin/audit/sessions/${id}`, 'GET'),
    getAuditStats: () => request<AuditStats>('/admin/audit/stats', 'GET'),
    getStories: () => request<StoryListItem[]>('/admin/stories', 'GET'),
    ingestStory: (filename: string) => request<StoryListItem>(`/admin/stories/${filename}/ingest`, 'POST'),
    deleteStory: (id: string) => request<{ deleted: string }>(`/admin/stories/${id}/delete`, 'DELETE'),
    exportStory: async (filename: string) => {
      const res = await apiFetchRaw(`/api/stories/${encodeURIComponent(filename)}/export`)
      const text = await res.text()
      const blob = new Blob([text], { type: 'application/yaml' })
      const url = URL.createObjectURL(blob)
      const cd = res.headers.get('Content-Disposition') ?? ''
      const match = cd.match(/filename="([^"]+)"/)
      const a = document.createElement('a')
      a.href = url
      a.download = match ? match[1] : `${filename}.yaml`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    getCharacters: () => request<CharacterListItem[]>('/admin/characters', 'GET'),
    getCharacter: (id: string) => request<CharacterDetail>(`/admin/characters/${id}`, 'GET'),
    updateCharacterPersonality: (id: string, personality: string) =>
      request<CharacterDetail>(`/admin/characters/${id}`, 'PUT', { personality }),
    setCharacterDynamicMotion: (id: string, dynamicMotion: boolean) =>
      request<{ dynamicMotion: boolean }>(`/admin/characters/${id}/dynamic-motion`, 'PUT', { dynamicMotion }),
    setCharacterGesturePoolEnabled: (id: string, gesturePoolEnabled: boolean) =>
      request<{ gesturePoolEnabled: boolean }>(`/admin/characters/${id}/gesture-pool-enabled`, 'PUT', { gesturePoolEnabled }),
    getCharacterGesturePoolStats: (id: string) =>
      request<GesturePoolStats>(`/admin/characters/${id}/gesture-pools/stats`, 'GET'),
    getCharacterGesturePool: (id: string) =>
      request<GesturePoolEntrySummary[]>(`/admin/characters/${id}/gesture-pools`, 'GET'),
    deleteGesturePoolEntry: (id: string) =>
      request<{ deleted: string }>(`/admin/gesture-pools/${id}`, 'DELETE'),
    clearCharacterGesturePool: (id: string) =>
      request<{ deleted: number }>(`/admin/characters/${id}/gesture-pools`, 'DELETE'),
    getStory: (filename: string) => request<StoryDetail>(`/admin/stories/${encodeURIComponent(filename)}/detail`, 'GET'),
    listDbPrefabs: () => request<DbPrefabDetail[]>('/admin/prefabs/', 'GET'),
    getDbPrefab: (id: string) => request<DbPrefabDetail>(`/admin/prefabs/${id}`, 'GET'),
    createDbPrefab: (characterName: string, name: string, phrases: string[], alternate: string, notFor: string[]) =>
      request<DbPrefabDetail>('/admin/prefabs/', 'POST', { characterName, name, phrases, alternate, notFor }),
    updateDbPrefab: (id: string, phrases: string[], alternate: string, notFor: string[]) =>
      request<DbPrefabDetail>(`/admin/prefabs/${id}`, 'PUT', { phrases, alternate, notFor }),
    deleteDbPrefab: (id: string) => apiFetch<void>(`/admin/prefabs/${id}`, { method: 'DELETE' }),
    setDbPrefabStories: (id: string, storyIds: string[]) =>
      request<DbPrefabDetail>(`/admin/prefabs/${id}/stories`, 'PUT', { storyIds }),
    listDbCachedResponses: () => request<DbCachedResponseDetail[]>('/admin/cached-responses/', 'GET'),
    getDbCachedResponse: (id: string) => request<DbCachedResponseDetail>(`/admin/cached-responses/${id}`, 'GET'),
    createDbCachedResponse: (name: string, phrases: string[], response: string, notFor: string[], animations: string) =>
      request<DbCachedResponseDetail>('/admin/cached-responses/', 'POST', { name, phrases, response, notFor, animations }),
    updateDbCachedResponse: (id: string, phrases: string[], response: string, notFor: string[], animations: string) =>
      request<DbCachedResponseDetail>(`/admin/cached-responses/${id}`, 'PUT', { phrases, response, notFor, animations }),
    deleteDbCachedResponse: (id: string) =>
      apiFetch<void>(`/admin/cached-responses/${id}`, { method: 'DELETE' }),
    setDbCachedResponseStories: (id: string, storyIds: string[]) =>
      request<DbCachedResponseDetail>(`/admin/cached-responses/${id}/stories`, 'PUT', { storyIds }),
    getCachedResponsesSettings: () =>
      request<{ similarity_threshold: number }>('/admin/cached-responses/settings', 'GET'),
    setCachedResponsesSettings: (similarity_threshold: number) =>
      request<{ similarity_threshold: number }>('/admin/cached-responses/settings', 'PUT', { similarity_threshold }),
    regenDbCachedResponseAudio: (id: string) =>
      request<{ regenerating: string }>(`/admin/cached-responses/${id}/regenerate`, 'POST'),
    deleteDbCachedResponseStoryAudio: (id: string, character: string, version: number) =>
      apiFetch<void>(
        `/admin/cached-responses/${id}/audio?character=${encodeURIComponent(character)}&version=${version}`,
        { method: 'DELETE' },
      ),
    getCharacterAnimConfig: (id: string) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/anim-config`, 'GET'),
    updateCharacterGestures: (id: string, gestures: string[]) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/gestures`, 'PUT', { gestures }),
    updateCharacterVocalCues: (id: string, vocalCues: string[]) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/vocal-cues`, 'PUT', { vocalCues }),
    updateCharacterGestureSynonyms: (id: string, gestureSynonyms: GestureSynonym[]) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/gesture-synonyms`, 'PUT', { gestureSynonyms }),
    updateCharacterA2FConfig: (id: string, config: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/characters/${id}/a2f-config`, 'PUT', config),
    getAppSettings: () =>
      request<{ defaultStory: string; defaultCharacter: string }>('/admin/app-settings', 'GET'),
    setAppSettings: (settings: { defaultStory: string; defaultCharacter: string }) =>
      request<{ defaultStory: string; defaultCharacter: string }>('/admin/app-settings', 'PUT', settings),
  }
}

// Me is the unified response from GET /api/me — matches the Go side's
// MeResponse struct. `method` is mostly relevant to audit logging; the web
// UI consumes `is_admin` for tier gating and `email` for the welcome
// message. `key_id` populates only on the X-API-Key auth path and is used
// for usage analytics joins, not gating.
export interface Me {
  id: string
  email?: string
  org_id?: string
  org_slug?: string
  roles: string[]
  is_admin: boolean
  method: 'jwt' | 'api_key'
  key_id?: string
}

export function getMe(): Promise<Me> {
  return apiFetch<Me>('/api/me')
}

export function getMySessions(): Promise<UserSessionBrief[]> {
  return apiFetch<UserSessionBrief[]>('/api/sessions')
}
